import { FC, useState } from "react";

import { AddCourseModal } from "./AddCourseModal";
import { CourseListItem } from "./CourseListItem";
import { SectionDetailTermView } from "./SectionDetailTermView";

import { COURSE_GROUPING_ID_QUERY_PARAM, COURSE_ID_QUERY_PARAM } from "components/constants";
import { EditableField } from "components/hover-widgets/EditableField";
import { AddCourseOptionMenu } from "components/menus/AddCourseOptionMenu";
import { CourseGroupingOptionsMenu } from "components/menus/CourseGroupingOptionsMenu";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, FolderIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { courseGroupingSelectors, courseSelectors } from "store/selectors";
import { selectCoursesByGroupingId } from "store/selectors/section";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";

type CourseGroupingViewProps = {
  eventPrefix?: string;
  canEdit: boolean;
};

export const CourseGroupingView: FC<CourseGroupingViewProps> = ({ eventPrefix, canEdit }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { getQueryParam } = useQueryParamsManager();
  const groupingId = getQueryParam(COURSE_GROUPING_ID_QUERY_PARAM);
  const courseId = getQueryParam(COURSE_ID_QUERY_PARAM);

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  const grouping = useAppSelector((state) => courseGroupingSelectors.selectById(state, groupingId));
  const coursesByGrouping = useListSelector((state) =>
    selectCoursesByGroupingId(state, groupingId),
  );
  const course = useAppSelector((state) => courseSelectors.selectById(state, courseId));
  const allCourses = useAppSelector(courseSelectors.selectAll);

  // If no grouping_id, show the detail view for the single course
  if (course && !grouping) {
    return (
      <SectionDetailTermView canEdit={canEdit} courseCode={course} eventPrefix={eventPrefix} />
    );
  }

  if (!grouping) {
    return null;
  }

  // If grouping_id exists, show the grouping with list of course codes
  const handleAddCourseCodeFromBanner = () => {
    showConfirmationModal({
      title: t("home.courses.add_course_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseModal
          defaultGroupingId={grouping.id}
          eventName={eventPrefix ? `${eventPrefix}Add Course Code` : undefined}
          localCourses={allCourses.map((code) => ({ order: code.order }))}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="h-full px-7 py-3">
      <h2 className="h2 my-0 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderIcon className="mb-[3px] text-blue" size="l" />

          <EditableField
            isEditing={isEditing}
            label={t("glossary.course_grouping")}
            textSize="h2"
            value={grouping.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) => {
              onTitleChanged("course_groupings", grouping.id, newTitle);
              toastSuccessMessage(t("home.toasts.grouping_renamed"));
            }}
          />
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <CourseGroupingOptionsMenu
              courseGrouping={grouping}
              eventPrefix={eventPrefix}
              onRename={() => setIsEditing(true)}
            />

            <AddCourseOptionMenu
              eventPrefix={eventPrefix}
              groupingId={grouping.id}
              localCourses={allCourses.map((code) => ({ order: code.order }))}
            />
          </div>
        )}
      </h2>

      {coursesByGrouping.length === 0 ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Add Course Code` : undefined}
                kind="blue"
                onClick={handleAddCourseCodeFromBanner}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
              </Button>
            ) : (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Enable Edit Mode` : undefined}
                kind="blue"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.courses.empty_grouping_banner_text")
            : t("home.courses.empty_grouping_banner_text_no_edit")}
        </Banner>
      ) : (
        <div role="list">
          {coursesByGrouping.map((code) => (
            <CourseListItem
              canEdit={canEdit}
              course={code}
              eventPrefix={eventPrefix}
              key={code.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
